/**
 * 设备检测工具
 */

/**
 * 检测是否为移动设备
 * @returns {boolean} 如果是移动设备返回 true，否则返回 false
 */
export const isMobile = (): boolean => {
  // 1. 通过 userAgent 检测
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // 移动设备的正则表达式
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i;
  
  // 2. 通过屏幕宽度检测（断点：768px）
  const isMobileWidth = window.innerWidth < 768;
  
  // 3. 通过 touch 事件检测
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // 综合判断：userAgent 匹配或者（屏幕宽度小于768且支持触摸）
  return mobileRegex.test(userAgent) || (isMobileWidth && hasTouch);
};

/**
 * 获取设备类型
 * @returns {'mobile' | 'pc'} 设备类型
 */
export const getDeviceType = (): 'mobile' | 'pc' => {
  return isMobile() ? 'mobile' : 'pc';
};

/**
 * 监听屏幕尺寸变化，动态更新设备类型
 * @param callback 回调函数，在设备类型改变时触发
 * @returns 清除监听器的函数
 */
export const onDeviceTypeChange = (
  callback: (deviceType: 'mobile' | 'pc') => void
): (() => void) => {
  let currentDeviceType = getDeviceType();
  
  const handleResize = () => {
    const newDeviceType = getDeviceType();
    if (newDeviceType !== currentDeviceType) {
      currentDeviceType = newDeviceType;
      callback(newDeviceType);
    }
  };
  
  window.addEventListener('resize', handleResize);
  
  // 返回清除监听器的函数
  return () => {
    window.removeEventListener('resize', handleResize);
  };
};

export type DesktopPlatform = 'mac' | 'windows64' | 'windows32';

/**
 * 识别当前桌面系统，用于 PC 客户端下载优先展示。
 * 无法判断时默认 Windows 64 位。
 */
export function detectDesktopPlatform(): DesktopPlatform {
  if (typeof navigator === 'undefined') {
    return 'windows64';
  }

  const ua = navigator.userAgent || '';
  const plat = navigator.platform || '';

  const isMac =
    (/Mac|Macintosh/.test(plat) || /Mac OS X/.test(ua)) && !/iPhone|iPad|iPod/.test(ua);
  if (isMac) {
    return 'mac';
  }

  const isWin = /Win/.test(plat) || /Windows/.test(ua);
  if (isWin) {
    const is64 = /Win64|x64|WOW64|amd64|x86_64/i.test(ua) || plat === 'Win64';
    return is64 ? 'windows64' : 'windows32';
  }

  return 'windows64';
}

